// Slide-in contact panel
let panelLastFocused = null;
let panelStep = 1;
let panelBookingNaturalHeight = 0;
const PANEL_STEP_COUNT = 3;

function panelPopulateServices() {
  const sel = document.getElementById('pf-service');
  if (!sel || sel.options.length > 1 || typeof SERVICE_INDEX === 'undefined') return;
  SERVICE_INDEX.forEach((s) => {
    const opt = document.createElement('option');
    opt.value = s.slug;
    opt.textContent = s.name;
    sel.appendChild(opt);
  });
}
panelPopulateServices();

// The booking widget's own internal steps (calendar / time-slot list /
// "enter details" form) are all very different heights, and per explicit
// request this must never scroll — so instead of sizing the box to the
// content, this shrinks (or grows) the content to the box. Widens/narrows
// the iframe versus its wrap's own width, then applies a single scale()
// factor (computed from the wrap's real box height vs. the content's real,
// unclipped height) so the visual result exactly fills the wrap on the
// height axis — height fills exactly as long as scale stays under the
// ceiling below, since naturalHeight * scale reduces to wrapRect.height
// whenever scale === wrapRect.height/naturalHeight. Capped at 1 (never
// grow) originally, which was fine when the box was narrow enough that
// content always ran taller than it — but once the panel-wide step gave
// this box much more width (see .panel-wide, main.css and panelSetStep
// below), short natural content (e.g. the calendar view, which reflows
// shorter at the new width) hit the 1x ceiling and just sat at its
// natural, smaller size with dead space below it. Raised to 1.5 so it
// grows to genuinely fill the space instead — still capped so it can't
// blow up into a cartoonish oversized widget on unusually short content.
//
// Width used to always fill the wrap's full width unconditionally
// (widthBudget/scale undoes itself once scaled back) — combined with the
// old 1x-capped height, that's what produced the "stretched and small"
// look: full-bleed wide but short, reading as a cheap insert rather than
// something that fills its box. Per explicit follow-up feedback, width is
// now capped at PANEL_BOOKING_MAX_WIDTH and centered in any leftover
// horizontal space instead of always stretching edge-to-edge — the wide
// panel-wide box gave this room to breathe, but the widget shouldn't
// actually render at that full raw width (which was also pushing its own
// responsive layout wider than intended, one contributor to the short
// natural height in the first place).
//
// Both constants below were tuned blind against a small (~1280px-wide)
// automation test window and never actually checked on a real, large
// desktop monitor — confirmed later from a real screenshot at 1920x878:
// the card rendered small with a large dead-space gap beneath it, on a
// box this session could not physically reproduce (the automation
// browser's own screen is capped smaller, and resize_window past that
// cap silently desyncs window.innerWidth from the CSS engine's own vw
// calculations — confirmed live, not assumed). Raised both ceilings
// since a genuinely short natural-content case (real on a big screen,
// per the screenshot) needs more headroom to actually fill a tall box
// instead of sitting capped-small with dead space around it; not
// re-verified live at the real box size that prompted this, since this
// session's automation window can't reach it — flag if the disproportion
// persists after this change.
const PANEL_BOOKING_MAX_WIDTH = 1200;
const PANEL_BOOKING_MAX_SCALE = 2;
function panelFitBookingFrame(naturalHeight) {
  const wrap = document.querySelector('.panel-booking-wrap');
  const frame = document.getElementById('panel-booking-frame');
  if (!wrap || !frame) return;
  if (naturalHeight) panelBookingNaturalHeight = naturalHeight;
  if (!panelBookingNaturalHeight) return;
  const wrapRect = wrap.getBoundingClientRect();
  if (wrapRect.width <= 0 || wrapRect.height <= 0) return;
  const scale = Math.min(PANEL_BOOKING_MAX_SCALE, wrapRect.height / panelBookingNaturalHeight);
  // A small safety margin: the target width below is a guess (we can't
  // read the cross-origin content's real rendered width), and the
  // widget's own layout has come in a few px wider than requested before,
  // silently clipped by .panel-booking-wrap's overflow:hidden with no
  // scrollbar to signal it. Reserving a few px keeps that from happening.
  const widthBudget = Math.min(wrapRect.width - 8, PANEL_BOOKING_MAX_WIDTH);
  frame.style.width = widthBudget / scale + 'px';
  frame.style.height = panelBookingNaturalHeight + 'px';
  frame.style.transform = `scale(${scale})`;
  // Center in any leftover space on both axes — horizontally when the
  // width cap above left room to spare, vertically if the 1.5x height
  // ceiling still leaves content shorter than the box (very short natural
  // content) — rather than leaving it pinned to the top-left corner.
  const leftoverX = wrapRect.width - 8 - widthBudget;
  const leftoverY = wrapRect.height - panelBookingNaturalHeight * scale;
  frame.style.left = leftoverX > 0 ? leftoverX / 2 + 'px' : '0';
  frame.style.top = leftoverY > 0 ? leftoverY / 2 + 'px' : '0';
}

let panelFitResizeTimer = null;
window.addEventListener('resize', () => {
  if (panelStep !== 3) return;
  clearTimeout(panelFitResizeTimer);
  panelFitResizeTimer = setTimeout(() => panelFitBookingFrame(), 150);
});

function panelSetStep(n) {
  panelStep = n;
  document.querySelectorAll('.panel-step').forEach((el) => {
    const s = Number(el.dataset.step);
    el.classList.remove('is-active', 'is-prev', 'is-next');
    if (s === n) el.classList.add('is-active');
    else if (s < n) el.classList.add('is-prev');
    else el.classList.add('is-next');
  });
  document.querySelectorAll('.panel-dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === n - 1);
  });
  // Reclaims the progress dots' vertical space for the booking widget —
  // every bit of extra box height lowers how much it has to shrink to fit.
  const progress = document.querySelector('.panel-progress');
  if (progress) progress.style.display = n === 3 ? 'none' : 'flex';
  // Collapses .panel-info to give the booking step the full panel width
  // instead of half (see .panel-wide, main.css) — per explicit request,
  // more width both directly needs less shrink for the same visual size,
  // and indirectly triggers the widget's own more compact side-by-side
  // responsive layout at wider viewports, lowering the height it needs too.
  const panelEl = document.getElementById('panel');
  panelEl.classList.toggle('panel-wide', n === 3);
  if (n === 3) {
    const frame = document.getElementById('panel-booking-frame');
    if (!frame.src) {
      frame.src = frame.dataset.src;
      if (typeof iFrameResize === 'function') {
        iFrameResize(
          {
            checkOrigin: false,
            // 'lowestElement' (the original choice here) measures the
            // geometric bottom edge of the lowest non-hidden descendant
            // regardless of ancestor overflow clipping — confirmed live
            // (2026-08-18, loading the real booking URL standalone,
            // outside the iframe, and replicating the same heuristic) that
            // this widget's own time-slot list is an internally-scrollable
            // region: it always contains far more <li> items than are
            // visible, so 'lowestElement' was picking up an off-screen
            // list item and reporting ~1487px instead of the real ~780px
            // visible height — a wrong, inflated reading that forced
            // panelFitBookingFrame() to shrink the whole widget (and its
            // text) far more than actually needed. 'bodyOffset' measures
            // document.body.offsetHeight directly, which correctly
            // reflects the widget's own real, intended layout height
            // (confirmed identical — 780px — across all 3 of its internal
            // steps: calendar, time confirm, enter-details form) and
            // respects its own internal scroll regions instead of trying
            // to unroll them.
            heightCalculationMethod: 'bodyOffset',
            onResized: (data) => panelFitBookingFrame(data.height),
          },
          frame
        );
      }
    }
    // .panel-info's own .6s collapse transition (main.css) means the very
    // first fit above — whether from onResized firing fast on a re-entry,
    // or from panelFitBookingFrame() below on a fresh load with no natural
    // height yet to act on — can land mid-transition and compute against
    // a stale, too-narrow wrapRect. A fixed [650,1500,3000]ms re-fit
    // schedule used to live here, but live testing (2026-08-18) showed
    // panelBookingNaturalHeight genuinely flip-flopping between loads on
    // the exact same code/network (~700px one load, ~1500px the next) —
    // GHL's widget appears to report an intermediate, not-yet-settled
    // height at least once before landing on its real one, and a fixed
    // delay has no way to know whether it caught the settled value or a
    // transient one. Replaced with panelWatchBookingSettle below, which
    // polls until two consecutive real remeasures agree instead of
    // guessing a delay is "probably" long enough.
    panelWatchBookingSettle(frame);
  }
}

// Polls frame.iFrameResizer.resize() (the library's own documented manual
// remeasure API) every 400ms, comparing panelBookingNaturalHeight before vs.
// after each call, until two consecutive reads agree (settled) or attempts
// run out (~4.8s ceiling) — see the comment above panelSetStep's call site
// for why a fixed delay schedule wasn't reliable enough. Each resize() call
// updates panelBookingNaturalHeight asynchronously via the onResized
// callback already wired in panelSetStep, so the actual comparison happens
// on the following tick rather than immediately after calling resize().
const PANEL_BOOKING_SETTLE_MAX_ATTEMPTS = 12;
function panelWatchBookingSettle(frame, attempt = 0) {
  if (attempt >= PANEL_BOOKING_SETTLE_MAX_ATTEMPTS) return;
  setTimeout(() => {
    const before = panelBookingNaturalHeight;
    if (frame.iFrameResizer) frame.iFrameResizer.resize();
    else panelFitBookingFrame();
    setTimeout(() => {
      if (panelBookingNaturalHeight === before) return;
      panelWatchBookingSettle(frame, attempt + 1);
    }, 50);
  }, 400);
}

function panelValidateStep(n) {
  const step = document.querySelector(`.panel-step[data-step="${n}"]`);
  const fields = step.querySelectorAll('input[required], select[required]');
  for (const f of fields) {
    if (!f.reportValidity()) return false;
  }
  return true;
}

// Captures a lead the moment they've given real contact info, rather than
// only on a completed booking — per the site's own documented gap: steps
// 1-2 never submitted anywhere, so anyone who filled them out but didn't
// finish the booking step (step 3) was simply lost. Fired from
// panelNextStep below on leaving step 1 (name/email/phone known) AND again
// on leaving step 2 (budget/service/timeline now known too) rather than
// only once at the end — same GHL workspace as the booking calendar's own
// widget, "Website Contact Form" workflow, Create/Update Contact action
// matches on email, so the second call enriches the same contact record
// instead of creating a duplicate. Deliberately fire-and-forget: a slow or
// failed webhook must never block the wizard's own step transition.
const PANEL_LEAD_WEBHOOK_URL =
  'https://services.leadconnectorhq.com/hooks/GIDG8mUMK8uutEFP9VMS/webhook-trigger/WXDhcDavZLNGsWBwtT19';
function panelSplitName(fullName) {
  const trimmed = fullName.trim();
  const spaceAt = trimmed.indexOf(' ');
  if (spaceAt === -1) return { firstName: trimmed, lastName: '' };
  return { firstName: trimmed.slice(0, spaceAt), lastName: trimmed.slice(spaceAt + 1).trim() };
}
function panelSubmitLead() {
  const nameEl = document.getElementById('pf-name');
  const emailEl = document.getElementById('pf-email');
  const phoneEl = document.getElementById('pf-phone');
  if (!nameEl || !emailEl || !phoneEl) return;
  const { firstName, lastName } = panelSplitName(nameEl.value);
  const budgetEl = document.getElementById('pf-budget');
  const serviceEl = document.getElementById('pf-service');
  const timelineEl = document.getElementById('pf-timeline');
  const payload = {
    firstName,
    lastName,
    email: emailEl.value,
    phone: phoneEl.value,
    budget: budgetEl && budgetEl.value ? budgetEl.options[budgetEl.selectedIndex].textContent : '',
    serviceInterest: serviceEl && serviceEl.value ? serviceEl.options[serviceEl.selectedIndex].textContent : '',
    timeline: timelineEl && timelineEl.value ? timelineEl.options[timelineEl.selectedIndex].textContent : '',
  };
  fetch(PANEL_LEAD_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

function panelNextStep() {
  if (!panelValidateStep(panelStep)) return;
  if (panelStep === 1 || panelStep === 2) panelSubmitLead();
  if (panelStep < PANEL_STEP_COUNT) panelSetStep(panelStep + 1);
}

function panelPrevStep() {
  if (panelStep > 1) panelSetStep(panelStep - 1);
}

// The panel is a solid, full-viewport overlay (position:fixed;inset:0) —
// the homepage behind it is fully hidden, but nothing was ever telling it
// to stop rendering. The hero video kept decoding/playing, and Lenis kept
// driving ScrollTrigger.update() on every scroll tick (lenis.on('scroll',
// ScrollTrigger.update), motion.js), which recalculates every pin/scrub/
// parallax on the page every frame, all for content the user can't see —
// real, invisible work stealing frame budget from the panel's own
// step-slide transitions, which is what read as lag switching steps.
// Stopping Lenis alone kills that cascade at the source, since nothing
// else drives ScrollTrigger without a real Lenis scroll event. Pausing
// videos too (only resuming ones we ourselves paused, via
// data-panel-paused, so we don't accidentally resume a video the page had
// already paused for its own reasons) covers the other real always-on
// cost. Deliberately does NOT also call gsap.globalTimeline.pause() —
// that was tried first, tested clean (a scrub animation mid-progress at
// pause time was confirmed to keep tracking scroll correctly afterward,
// live-verified via ScrollTrigger progress values), but per explicit user
// report of a felt regression across the whole site (not just the panel)
// after that version shipped, narrowed the fix to just the one surgical
// piece that's provably responsible for the actual expensive work
// (Lenis -> ScrollTrigger.update) rather than a blanket pause touching
// every one of the 18+ tweens/ScrollTriggers on the page — lower risk for
// a marginal loss (only short-lived one-shot reveal tweens don't get
// paused now, which cost little regardless since they finish on their
// own in ~1s and aren't scroll-driven). The Lenis check is defensive/
// no-op on pages that don't load motion.js (service/project pages only
// load raw GSAP+ScrollTrigger, for the CTA fade, no Lenis).
function panelPauseBackground() {
  document.querySelectorAll('video').forEach((v) => {
    if (!v.paused) {
      v.dataset.panelPaused = '1';
      v.pause();
    }
  });
  if (window.__sixthSenseMotion && window.__sixthSenseMotion.lenis) {
    window.__sixthSenseMotion.lenis.stop();
  }
}

function panelResumeBackground() {
  document.querySelectorAll('video[data-panel-paused]').forEach((v) => {
    delete v.dataset.panelPaused;
    v.play();
  });
  if (window.__sixthSenseMotion && window.__sixthSenseMotion.lenis) {
    window.__sixthSenseMotion.lenis.start();
  }
}

function openPanel() {
  window.location.assign('/pages/consultation');
} {
  panelLastFocused = document.activeElement;
  const panel = document.getElementById('panel');
  panel.classList.add('open');
  panelSetStep(1);
  panelPauseBackground();
  const firstField = panel.querySelector('input, textarea');
  if (firstField) firstField.focus();
}

// Once step 3 has been reached, the booking iframe's `src` guard
// (`if (!frame.src)`, panelSetStep above) means it only ever loads ONCE —
// nothing ever tore it back down again, not on going Back to step 1/2,
// not on closing the panel. It just sat there hidden (translateX'd
// off-screen with the rest of the inactive step), but very much alive:
// the GHL widget's own JS keeps running, and iframeResizer's own
// contentWindow script keeps a ResizeObserver/MutationObserver watching
// for size changes indefinitely, completely independent of whether our
// panel is even open — for the rest of that page's session, until a real
// navigation destroys the whole document. Per user report ("never lagged
// until [using the contact page]"), this is the most likely real
// culprit: a heavy third-party single-page app kept alive and running in
// the background long after anyone stopped looking at it. Tearing it
// down on close (and letting panelSetStep re-load it fresh next time
// step 3 is genuinely reached) fixes that at the source.
function panelUnloadBookingFrame() {
  const frame = document.getElementById('panel-booking-frame');
  if (!frame || !frame.src) return;
  const wrap = frame.parentElement;
  const dataSrc = frame.dataset.src;
  // iFrameResizer's own close() doesn't just stop listening — confirmed
  // live it removes the iframe element from the DOM outright (a more
  // thorough teardown than a plain removeAttribute('src') would have
  // been: the whole child document, and every observer/timer/connection
  // running inside it, is destroyed, not just visually hidden). That
  // means panelSetStep's own `document.getElementById('panel-booking-
  // frame')` lookup would find nothing on the next visit to step 3 unless
  // a fresh replacement iframe (same id/classes/data-src) is put back in
  // its place — built here rather than left for panelSetStep to discover
  // missing, so the guard there (`if (!frame.src)`) keeps working exactly
  // as before with zero other changes needed.
  if (frame.iFrameResizer) {
    frame.iFrameResizer.close();
  } else {
    frame.removeAttribute('src');
  }
  const fresh = document.createElement('iframe');
  fresh.id = 'panel-booking-frame';
  fresh.className = 'panel-booking-frame';
  fresh.dataset.src = dataSrc;
  fresh.loading = 'lazy';
  fresh.title = 'Book a consultation';
  wrap.appendChild(fresh);
  panelBookingNaturalHeight = 0;
}

function closePanel() {
  document.getElementById('panel').classList.remove('open');
  panelResumeBackground();
  panelUnloadBookingFrame();
  if (panelLastFocused) panelLastFocused.focus();
}

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && document.getElementById('panel').classList.contains('open')) {
    closePanel();
  }
});

// Fires the Google Ads "Book appointment" conversion once the GHL booking
// widget (#panel-booking-frame, cross-origin at api.leadconnectorhq.com)
// confirms a real completed booking. The widget's own submit/click events
// are invisible to us across the origin boundary, but it broadcasts
// completion via postMessage: a 2-element array,
// ['msgsndr-booking-complete', {calendarId: '...'}] (confirmed pattern,
// not something LeadConnector documents officially). Registered once here
// at script load, not per iframe open/close, since panelUnloadBookingFrame
// tears down and rebuilds the iframe on every close/reopen — a single
// origin+shape-filtered listener covers every booking regardless of how
// many times the panel or iframe gets recreated across the page's life.
window.addEventListener('message', (event) => {
  if (event.origin !== 'https://api.leadconnectorhq.com') return;
  if (!Array.isArray(event.data) || event.data[0] !== 'msgsndr-booking-complete') return;
  if (typeof gtag !== 'function') return;
  gtag('event', 'conversion', {
    send_to: 'AW-16888482530/3hOaCOyV75YbEOKVh_U-',
    value: 1.0,
    currency: 'USD',
  });
});
